import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Grid,
  Modal,
  MenuItem,
  FormControl,
  Typography,
  InputLabel,
  Select,
} from '@mui/material';

import * as HrActions from '../../../../../store/actions/hr.action';
import { getHrState } from '../../../../../store/selector/hr.selector';
import { IFilterModal } from '../../../../interfaces/modal.interface';
import {
  FILTER_MONTHS_CONSTANT,
  FILTER_STATUSES_CONSTANT,
} from '../../../../constants/filter.constant';

const styles = {
  modalContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { sm: 600, xs: '100%' },
    bgcolor: 'background.paper',
    borderRadius: { sm: '0.5rem', xs: 0 },
    boxShadow: 24,
    overflowY: 'visible',
    p: 4,
  },

  confirmLogoutFormTitle: { marginBottom: '0.5rem' },

  modalListContainer: {
    marginTop: '1rem',
    display: 'flex',
    justifyContent: 'space-between',
    paddingY: '0.5rem',
  },

  modalListItem: {
    display: 'flex',
    justifyContent: 'space-between',
  },

  modalCloseButton: {
    padding: '0.5rem 3rem',
    borderRadius: '2.5rem',
    width: { sm: 'auto', xs: '100%' },
  },
};

const FilterModal = ({
  isModalOpen,
  onClickModal,
}: {
  isModalOpen: boolean;
  onClickModal: (e: string) => void;
}) => {
  const dispatch = useDispatch();
  /* eslint-disable camelcase */
  const {
    reports_query,
    reports: { departments, provinces, reasons, years },
  } = useSelector(getHrState);
  useEffect(() => {
    dispatch(HrActions.DashboardStaticData());
  }, []);

  const [filter, setFilter] = useState<IFilterModal>({
    status: '',
    province: '',
    department: '',
    reason: '',
    month: '',
    year: '',
  });
  const [hasFilter, setHasFilter] = useState<boolean>(false);

  const filterTable = () => {
    if (reports_query) {
      dispatch(
        HrActions.DashboardApplicationData({
          ...reports_query,
          department: filter.department,
          month: filter.month,
          province: filter.province,
          reason: filter.reason,
          signatory: '',
          status: filter.status,
          year: filter.year,
        }),
      );
      setHasFilter(true);
    }
  };

  return (
    <Modal open={isModalOpen}>
      <Box sx={styles.modalContainer}>
        <Box>
          <Typography sx={{ fontSize: '1.75rem', mb: '2rem' }} color='primary'>
            Filter Reports
          </Typography>

          <Grid container sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Grid item xs={12} sx={{ mb: '1rem' }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>

                <Select
                  fullWidth
                  label='Status'
                  value={filter.status}
                  onChange={(e) => {
                    setFilter((p) => ({
                      ...p,
                      status: e.target.value,
                    }));
                  }}
                >
                  {FILTER_STATUSES_CONSTANT.map(({ label, value }, idx) => (
                    <MenuItem key={idx} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sx={{ mb: '1rem' }}>
              <FormControl fullWidth>
                <InputLabel>Province</InputLabel>

                <Select
                  fullWidth
                  label='Province'
                  value={filter.province}
                  onChange={(e) => {
                    setFilter((p) => ({
                      ...p,
                      province: e.target.value,
                    }));
                  }}
                >
                  <MenuItem value=''>All</MenuItem>

                  {provinces.map((province: string, idx: number) => (
                    <MenuItem key={idx} value={province}>
                      {province}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sx={{ mb: '1rem' }}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>

                <Select
                  fullWidth
                  label='Department'
                  value={filter.department}
                  onChange={(e) => {
                    setFilter((p) => ({
                      ...p,
                      department: e.target.value,
                    }));
                  }}
                >
                  <MenuItem value=''>All</MenuItem>

                  {departments.map((dept: string, idx: number) => (
                    <MenuItem key={idx} value={dept}>
                      {dept}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item md={5.9} xs={12} sx={{ mb: '1rem' }}>
              <FormControl fullWidth>
                <InputLabel>Month</InputLabel>

                <Select
                  fullWidth
                  label='Month'
                  value={filter.month}
                  onChange={(e) => {
                    setFilter((p) => ({
                      ...p,
                      month: e.target.value,
                    }));
                  }}
                >
                  {FILTER_MONTHS_CONSTANT.map(({ label, value }, idx) => (
                    <MenuItem key={idx} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item md={5.9} xs={12} sx={{ mb: '1rem' }}>
              <FormControl fullWidth>
                <InputLabel>Year</InputLabel>

                <Select
                  fullWidth
                  label='Year'
                  value={filter.year}
                  onChange={(e) => {
                    setFilter((p) => ({
                      ...p,
                      year: e.target.value.toString(),
                    }));
                  }}
                >
                  <MenuItem value=''>All</MenuItem>

                  {years.map((year: number, idx: number) => (
                    <MenuItem key={idx} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sx={{ mb: '2rem' }}>
              <FormControl fullWidth>
                <InputLabel>Reason</InputLabel>

                <Select
                  fullWidth
                  label='Reason'
                  value={filter.reason}
                  onChange={(e) => {
                    setFilter((p) => ({
                      ...p,
                      reason: e.target.value,
                    }));
                  }}
                >
                  <MenuItem value=''>All</MenuItem>

                  {reasons.map((reason: string, idx: number) => (
                    <MenuItem key={idx} value={reason}>
                      {reason}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant='contained'
            sx={{ ...styles.modalCloseButton, mr: '1rem' }}
            onClick={() => {
              onClickModal('confirm'), filterTable();
            }}
            disableElevation
          >
            Filter
          </Button>

          <Button
            color='inherit'
            variant='contained'
            sx={styles.modalCloseButton}
            onClick={() => {
              if (!hasFilter) {
                onClickModal('cancel');
              } else {
                setFilter({
                  status: '',
                  province: '',
                  department: '',
                  reason: '',
                  month: '',
                  year: '',
                });

                setHasFilter(false);
              }
            }}
            disableElevation
          >
            {hasFilter ? 'Clear' : 'Cancel'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default FilterModal;
